import { useCssVars as _useCssVars, defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, createVNode as _createVNode } from "vue"

const _hoisted_1 = /*#__PURE__*/_createTextVNode(" Tap when the background is the same color! ")
const _hoisted_2 = /*#__PURE__*/_createTextVNode(" Tap when the background is the same color! ")
const _hoisted_3 = /*#__PURE__*/_createTextVNode(" Tap the correct color when the background matches it! ")
const _hoisted_4 = {
  key: 1,
  class: "container-centered"
}
const _hoisted_5 = { class: "k-phase-btn-container" }

import * as GameTypes from 'types/game.types';
import { reactive, ref } from 'vue';
import { useModule } from 'src/modules/modules';
import { getRandomNonUglyColor } from 'util/color-util';
import chroma from 'chroma-js';
import GameContentArea from '../game-content-area.vue';
import PhasingBackground from '../phasing-background.vue';
import PhasingButton from '../phasing-button.vue';
import ExandingCircle from '../expanding-circle.vue';
import ContrastColorText from '../contrast-color-text.vue';
import ColorPicker from '../color-picker.vue';
import { useMotions } from '@vueuse/motion';
import { getGameSetTransitions, gameSetLeave } from '../transitions';
import { sample, random, clamp, shuffle } from 'lodash';

type PhaseBtnExpose = { stop: () => chroma.Color, leave: (delay?: number) => Promise<void> }


export default /*#__PURE__*/_defineComponent({
  __name: 'color-phase',
  emits: ['next', 'fail'],
  setup(__props, { expose, emit }) {

_useCssVars(_ctx => ({
  "6ed0b954": (_unref(phaseBtnSize))
}))

const { game, audio } = useModule();

const failed = ref(false);
const targetColor = ref(getRandomNonUglyColor());
const variation = ref(1);
const colors = ref<chroma.Color[]>([]);
const phaseStepBackground = ref<GameTypes.PhaseStep[]>([]);
const phaseSteps = ref<GameTypes.PhaseStep[][]>([]);
const bgComponentRef = ref();
const containerRef = ref();
const circleLeaveFxRef = ref();
const phaseBtnRefs = ref<PhaseBtnExpose[]>([]);
const picker = ref();
const state = ref<{
    distanceMin: number,
    distanceMax: number,
    phaseDurationMin: number,
    phaseDurationMax: number,
    colorCount: number,
    threshold: number,
    shape: 'circle' | 'square'
}>({
    distanceMin: 49,
    distanceMax: 100,
    phaseDurationMin: 750,
    phaseDurationMax: 4000,
    colorCount: 1,
    threshold: 12,
    shape: 'square'
})

/**
 * Tap the correct color as the background phases through it.
 */
const variation1 = () => {
    variation.value = 1;
    if (game.matchState.difficulty === 0){
        state.value = {
            distanceMin: 49,
            distanceMax: 100,
            phaseDurationMin: 750,
            phaseDurationMax: 4000,
            colorCount: 1,
            threshold: 12,
            shape: 'square'
        }
    } else if (game.matchState.difficulty === 1){
        state.value = {
            distanceMin: 49,
            distanceMax: 100,
            phaseDurationMin: 750,
            phaseDurationMax: 4000,
            colorCount: 4,
            threshold: 10,
            shape: 'square'
        }
    } else if (game.matchState.difficulty === 2){
        state.value = {
            distanceMin: 49,
            distanceMax: 100,
            phaseDurationMin: 750,
            phaseDurationMax: 4000,
            colorCount: 8,
            threshold: 9,
            shape: 'square'
        }
    } else if (game.matchState.difficulty === 3){
        state.value = {
            distanceMin: 49,
            distanceMax: 100,
            phaseDurationMin: 750,
            phaseDurationMax: 4000,
            colorCount: 12,
            threshold: 8,
            shape: 'square'
        }
    } else if (game.matchState.difficulty >= 4){
        state.value = {
            distanceMin: 49,
            distanceMax: 100,
            phaseDurationMin: 750,
            phaseDurationMax: 4000,
            colorCount: 16,
            threshold: 7,
            shape: 'square'
        }
    }

    /**
     * Generate a list of colors to choose from and a
     */
    colors.value[0] = targetColor.value;
    for (let i = 1; i < state.value.colorCount; i++){
        colors.value.push(getRandomNonUglyColor());
    }
    colors.value = Phaser.Utils.Array.Shuffle(colors.value);

    /**
     * Randomly get a basic phase step set which either phases directly into the 
     * target color or phases to an intermediate color first.
     */
    phaseStepBackground.value = sample([
        [
            { duration: 'random', color: 'random' },
            { duration: 'random', color: targetColor.value.hex() }
        ],
        [
            { duration: 'random', color: targetColor.value.hex() },
            { duration: 'random', color: 'random' },
        ]
    ])!;
}

/**
 * Generates random phase steps where none of the steps will (probably) ever match the target color.
 */
const getRandomSteps = (count: number) => {
    let steps : GameTypes.PhaseStep[] = [];
    for (let i = 0; i < count; i++){
        steps.push({ duration: random(750, 4000), color: 'random' });
    }
    return steps;
}

/**
 * Generates a series of phase steps where one of them is the target color.
 * These same phase steps are used for both the background and the button, however the 
 * "random" color is dynamically generated for either.
 */
const getTargetSteps = (durationMin: number = 750, durationMax: number = 4000) => {
    let stepCount = random(3, 5);
    let targetIndex = random(2, stepCount - 1); //target should never be the first step
    let steps : GameTypes.PhaseStep[] = [];
    for (let i = 0; i < stepCount; i++){
        if (i === targetIndex){
            steps.push({ duration: random(durationMin, durationMax), color: targetColor.value.hex() });
        } else {
            steps.push({ duration: random(durationMin, durationMax), color: 'random' });
        }
    }
    return steps;
}

const variation2 = () => {
    variation.value = 2;
    if (game.matchState.difficulty < 2 ){
        state.value = {
            distanceMin: 49,
            distanceMax: 100,
            phaseDurationMin: 750,
            phaseDurationMax: 4000,
            colorCount: 1,
            threshold: 5,
            shape: 'square'
        }
    } else if (game.matchState.difficulty === 2){
        state.value = {
            distanceMin: 49,
            distanceMax: 100,
            phaseDurationMin: 750,
            phaseDurationMax: 3000,
            colorCount: 3,
            threshold: 5,
            shape: sample(['circle', 'square'])! 
        }
    } else if (game.matchState.difficulty === 3){
        state.value = {
            distanceMin: 49,
            distanceMax: 100,
            phaseDurationMin: 750,
            phaseDurationMax: 3000,
            colorCount: 4,
            threshold: 5,
            shape: sample(['circle', 'square'])! 
        }
    } else if (game.matchState.difficulty > 3){
        state.value = {
            distanceMin: 49,
            distanceMax: 100,
            phaseDurationMin: 750,
            phaseDurationMax: 2750,
            colorCount: 6,
            threshold: 5,
            shape: sample(['circle', 'square'])! 
        }
    } 

    /**
     * Generates multiples sets of phase steps, including one set which will contain our target color and be
     * used for both the background and one of the buttons (and so will be synchronized).
     */
    let targetSteps = getTargetSteps(state.value.phaseDurationMin, state.value.phaseDurationMax);
    let steps : GameTypes.PhaseStep[][] = [targetSteps];
    for (let i = 1; i < state.value.colorCount; i++){
        steps.push(getRandomSteps(5));
    }

    phaseStepBackground.value = targetSteps;
    phaseSteps.value = shuffle(steps);
}

if (game.matchState.difficulty === 0) variation1()
else sample([variation1, variation2])!();

//variation2();

const handlePhaseButtonEnter = (exposed: PhaseBtnExpose) =>  phaseBtnRefs.value.push(exposed);
const phaseButtonsLeave = () => {
    return Promise.all(phaseBtnRefs.value.map((exp, i) => exp.leave((25 - i) * i)));
}

const leave = () => {
    return Promise.all([
        picker.value?.leave(),
        phaseButtonsLeave(),
        circleLeaveFxRef?.value.leave(),
        containerRef?.value.leave()
    ]);
}

/**
 * Handler for when a color from the color picker is selected. This one requires that the color
 * selected is our target color, regardless of how close the deltaE value is to the color selected.
 */
const buttonClicked = ref(false);
const handleColorSelected = async (selected: chroma.Color) => {
    if (buttonClicked.value) return; //Ensure we only handle one click
    buttonClicked.value = true;

    if (bgComponentRef.value) {
        let bgColor : chroma.Color = bgComponentRef.value.stop();
        let isTargetColor = selected.hex() === targetColor.value.hex();
        let distance = chroma.deltaE(selected, bgColor);
        if (isTargetColor && distance <= state.value.threshold){
            game.matchState.precisionRounds += 1;

            let multipliers = [];
            if (distance <= 1){
                game.matchState.precisionCount += 1;
                multipliers.push({
                    factor: 4,
                    label: 'Precision Bonus',
                })
            }

            emit('next', { scoreMultipliers: multipliers });
            return;
        }
        failed.value = true; //Will hide bg so that our red failure FX will not be obscured
        emit('fail');
    }
}

/**
 * Handler for when the phase button is pressed. This one compares the deltaE value of two colors
 * for which we don't have an absolute color value for.
 */
const handlePhaseButtonDown = async (selectedColor: string) => {
    if (buttonClicked.value) return; //Ensure we only handle one click
    buttonClicked.value = true;

    //let selected : chroma.Color = phaseBtnRef.value.stop();
    let bgColor : chroma.Color = bgComponentRef.value.stop();
    let distance = chroma.deltaE(selectedColor, bgColor);
    if (distance <= state.value.threshold){
        game.matchState.precisionRounds += 1;

        let multipliers = [];
        if (distance <= 1){
            game.matchState.precisionCount += 1;
            multipliers.push({
                factor: 4,
                label: 'Precision Bonus',
            })
        }

        emit('next', { scoreMultipliers: multipliers });
        return;
    }
    failed.value = true; 
    emit('fail');
}

let phaseBtnSize = "60%";
if (state.value.colorCount > 1){
    phaseBtnSize = "40%";
}

// const phaseBtnSize = `${clamp(100 / (state.value.colorCount + 1), 15, 60)}%`;

expose({
    leave
})

console.log(`Color Phase: Variation ${variation.value}, Difficulty ${game.matchState.difficulty}`);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(GameContentArea, {
    ref_key: "containerRef",
    ref: containerRef,
    "high-contrast": true,
    "infinite-time": true,
    onSkip: _cache[0] || (_cache[0] = () => emit('next', { skipped: true }))
  }, {
    instruction: _withCtx(() => [
      (variation.value === 1 && colors.value.length === 1)
        ? (_openBlock(), _createBlock(ContrastColorText, {
            key: 0,
            color: targetColor.value
          }, {
            default: _withCtx(() => [
              _hoisted_1
            ]),
            _: 1
          }, 8, ["color"]))
        : (variation.value === 2)
          ? (_openBlock(), _createBlock(ContrastColorText, {
              key: 1,
              color: _unref(chroma)('#0000006c')
            }, {
              default: _withCtx(() => [
                _hoisted_2
              ]),
              _: 1
            }, 8, ["color"]))
          : (_openBlock(), _createBlock(ContrastColorText, {
              key: 2,
              color: _unref(chroma)('#0000006c')
            }, {
              default: _withCtx(() => [
                _hoisted_3
              ]),
              _: 1
            }, 8, ["color"]))
    ]),
    content: _withCtx(() => [
      (variation.value === 1)
        ? (_openBlock(), _createBlock(ColorPicker, {
            key: 0,
            ref_key: "picker",
            ref: picker,
            colors: colors.value,
            onSelect: handleColorSelected
          }, null, 8, ["colors"]))
        : (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(phaseSteps.value, (steps, i) => {
                return (_openBlock(), _createBlock(PhasingButton, {
                  targetColor: targetColor.value,
                  steps: steps,
                  shape: state.value.shape,
                  delay: (25 - i) * i,
                  class: "k-phase-btn",
                  onSelect: handlePhaseButtonDown,
                  onEnter: handlePhaseButtonEnter
                }, null, 8, ["targetColor", "steps", "shape", "delay"]))
              }), 256))
            ])
          ])),
      (!failed.value)
        ? (_openBlock(), _createBlock(PhasingBackground, {
            key: 2,
            ref_key: "bgComponentRef",
            ref: bgComponentRef,
            targetColor: targetColor.value,
            steps: phaseStepBackground.value
          }, null, 8, ["targetColor", "steps"]))
        : _createCommentVNode("", true),
      _createVNode(ExandingCircle, {
        ref_key: "circleLeaveFxRef",
        ref: circleLeaveFxRef
      }, null, 512)
    ]),
    _: 1
  }, 512))
}
}

})